/**
 * Procedural textures and configuration for the debug material panel.
 *
 * All textures are generated on a 256×256 canvas and cached as Three.js
 * CanvasTextures. The debug config is stored module-level so that
 * {@link createViewportMaterial} can read it without a React hook.
 */

import * as THREE from "three/webgpu";
import { DEFAULT_MATERIAL } from "../modeling/types.ts";

// ---------------------------------------------------------------------------
// Debug material configuration
// ---------------------------------------------------------------------------

export interface DebugMaterialConfig {
  color: string;
  roughness: number;
  metalness: number;
  emissiveColor: string;
  emissiveIntensity: number;
  flatShading: boolean;
  albedoMapEnabled: boolean;
  albedoMapScale: number;
  normalMapEnabled: boolean;
  normalMapIntensity: number;
  roughnessMapEnabled: boolean;
  roughnessMapScale: number;
  metallicMapEnabled: boolean;
  emissionMapEnabled: boolean;

  // Physical material properties
  opacity: number;
  specularIntensity: number;
  clearcoat: number;
  clearcoatRoughness: number;
  sheen: number;
  sheenRoughness: number;
  sheenColor: string;
  transmission: number;
  ior: number;
  thickness: number;
}

export const DEFAULT_DEBUG_MATERIAL: DebugMaterialConfig = {
  color: DEFAULT_MATERIAL.color,
  roughness: DEFAULT_MATERIAL.roughness,
  metalness: DEFAULT_MATERIAL.metalness,
  emissiveColor: "#000000",
  emissiveIntensity: 0.0,
  flatShading: false,
  albedoMapEnabled: false,
  albedoMapScale: 8,
  normalMapEnabled: false,
  normalMapIntensity: 1.0,
  roughnessMapEnabled: false,
  roughnessMapScale: 8,
  metallicMapEnabled: false,
  emissionMapEnabled: false,
  opacity: 1.0,
  specularIntensity: 0.5,
  clearcoat: 0,
  clearcoatRoughness: 0,
  sheen: 0,
  sheenRoughness: 1.0,
  sheenColor: "#000000",
  transmission: 0,
  ior: 1.5,
  thickness: 0,
};

// ---------------------------------------------------------------------------
// Procedural texture generators (256×256 canvases, cached)
// ---------------------------------------------------------------------------

const TEX_SIZE = 256;

// Static (parameter-free) textures are keyed by their pattern name; each key
// is ever written once, so the Map is bounded by the set of static patterns.
const texCache = new Map<string, THREE.CanvasTexture>();

// Single-slot caches for scale-parameterized patterns. The debug panel's
// scale slider has ~16 discrete steps; a naïve key-by-scale cache accumulates
// one texture per value the user passes through, even though only one is ever
// displayed. Keeping a single slot per pattern caps memory at one texture per
// pattern and disposes the predecessor on slot update.
let checkerSlot: { scale: number; tex: THREE.CanvasTexture } | null = null;
let roughCheckerSlot: { scale: number; tex: THREE.CanvasTexture } | null = null;

function getOrCreate(
  key: string,
  fn: () => THREE.CanvasTexture,
): THREE.CanvasTexture {
  let tex = texCache.get(key);
  if (!tex) {
    tex = fn();
    texCache.set(key, tex);
  }
  return tex;
}

/**
 * Dispose every cached debug texture and reset the cache slots. Callable
 * from HMR or a debug-teardown path. Safe to call repeatedly — subsequent
 * texture requests will regenerate lazily.
 */
export function clearDebugTextureCache(): void {
  for (const tex of texCache.values()) tex.dispose();
  texCache.clear();
  if (checkerSlot) {
    checkerSlot.tex.dispose();
    checkerSlot = null;
  }
  if (roughCheckerSlot) {
    roughCheckerSlot.tex.dispose();
    roughCheckerSlot = null;
  }
}

/**
 * Checkerboard albedo texture.
 * Alternating white/dark-gray squares at the given scale (squares per side).
 */
export function createCheckerboardTexture(scale: number): THREE.CanvasTexture {
  if (checkerSlot && checkerSlot.scale === scale) return checkerSlot.tex;
  if (checkerSlot) checkerSlot.tex.dispose();
  const canvas = document.createElement("canvas");
  canvas.width = TEX_SIZE;
  canvas.height = TEX_SIZE;
  const ctx = canvas.getContext("2d")!;

  const cellSize = TEX_SIZE / scale;
  for (let y = 0; y < scale; y++) {
    for (let x = 0; x < scale; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? "#e0e0e0" : "#404040";
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  checkerSlot = { scale, tex };
  return tex;
}

/**
 * Procedural normal map — sinusoidal bumps.
 * Creates a tangent-space normal map with wavy ridges.
 */
export function createProceduralNormalMap(): THREE.CanvasTexture {
  return getOrCreate("procNormal", () => {
    const canvas = document.createElement("canvas");
    canvas.width = TEX_SIZE;
    canvas.height = TEX_SIZE;
    const ctx = canvas.getContext("2d")!;
    const imageData = ctx.createImageData(TEX_SIZE, TEX_SIZE);
    const data = imageData.data;

    const freq = 6;
    for (let y = 0; y < TEX_SIZE; y++) {
      for (let x = 0; x < TEX_SIZE; x++) {
        const u = x / TEX_SIZE;
        const v = y / TEX_SIZE;

        // Derivatives of a sine-wave height field
        const dhdx =
          Math.cos(u * freq * Math.PI * 2) *
          Math.sin(v * freq * Math.PI * 2) *
          0.5;
        const dhdy =
          Math.sin(u * freq * Math.PI * 2) *
          Math.cos(v * freq * Math.PI * 2) *
          0.5;

        // Tangent-space normal: (-dhdx, -dhdy, 1) normalized, then encoded to [0,255]
        const len = Math.sqrt(dhdx * dhdx + dhdy * dhdy + 1);
        const nx = (-dhdx / len) * 0.5 + 0.5;
        const ny = (-dhdy / len) * 0.5 + 0.5;
        const nz = (1 / len) * 0.5 + 0.5;

        const idx = (y * TEX_SIZE + x) * 4;
        data[idx] = Math.round(nx * 255);
        data[idx + 1] = Math.round(ny * 255);
        data[idx + 2] = Math.round(nz * 255);
        data[idx + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.LinearSRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    return tex;
  });
}

/**
 * Checkerboard roughness texture — alternating smooth (black) and rough (white)
 * squares at the given scale (squares per side).
 */
export function createRoughnessCheckerboardTexture(
  scale: number,
): THREE.CanvasTexture {
  if (roughCheckerSlot && roughCheckerSlot.scale === scale) {
    return roughCheckerSlot.tex;
  }
  if (roughCheckerSlot) roughCheckerSlot.tex.dispose();
  const canvas = document.createElement("canvas");
  canvas.width = TEX_SIZE;
  canvas.height = TEX_SIZE;
  const ctx = canvas.getContext("2d")!;

  const cellSize = TEX_SIZE / scale;
  for (let y = 0; y < scale; y++) {
    for (let x = 0; x < scale; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? "#1a1a1a" : "#e6e6e6";
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.LinearSRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  roughCheckerSlot = { scale, tex };
  return tex;
}

/**
 * Procedural metallic map — vertical gradient from non-metallic (bottom) to metallic (top).
 */
export function createMetallicGradientTexture(): THREE.CanvasTexture {
  return getOrCreate("metalGrad", () => {
    const canvas = document.createElement("canvas");
    canvas.width = TEX_SIZE;
    canvas.height = TEX_SIZE;
    const ctx = canvas.getContext("2d")!;

    const grad = ctx.createLinearGradient(0, TEX_SIZE, 0, 0);
    grad.addColorStop(0, "#000"); // dielectric
    grad.addColorStop(1, "#fff"); // metallic
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, TEX_SIZE, TEX_SIZE);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.LinearSRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    return tex;
  });
}

/**
 * Procedural emission map — radial glow from center.
 */
export function createEmissionGlowTexture(): THREE.CanvasTexture {
  return getOrCreate("emitGlow", () => {
    const canvas = document.createElement("canvas");
    canvas.width = TEX_SIZE;
    canvas.height = TEX_SIZE;
    const ctx = canvas.getContext("2d")!;

    const grad = ctx.createRadialGradient(
      TEX_SIZE / 2,
      TEX_SIZE / 2,
      0,
      TEX_SIZE / 2,
      TEX_SIZE / 2,
      TEX_SIZE / 2,
    );
    grad.addColorStop(0, "#ffffff");
    grad.addColorStop(0.5, "#888888");
    grad.addColorStop(1, "#000000");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, TEX_SIZE, TEX_SIZE);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    return tex;
  });
}
